/**
 * Helpers for mapping public form validation errors to field UI.
 */

export function parseFormSchemaJson(raw: string | undefined): unknown | null {
	if (!raw?.trim()) {
		return null;
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		return typeof parsed === "object" &&
			parsed !== null &&
			!Array.isArray(parsed)
			? parsed
			: null;
	} catch {
		return null;
	}
}

export function isFieldErrorsRecord(
	value: unknown
): value is Record<string, string> {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return false;
	}
	return Object.values(value).every((entry) => typeof entry === "string");
}

export function validationErrorCount(errors: Record<string, string>): number {
	return Object.keys(errors).length;
}

/** Short form-level summary when one or more fields failed validation. */
export function validationSummaryMessage(
	errors: Record<string, string>
): string {
	const count = validationErrorCount(errors);
	if (count === 0) {
		return "";
	}
	if (count === 1) {
		return "Please fix the error above.";
	}
	// The summary renders after the fields (PublicFormAlerts), so errors are above it.
	return `Please fix the ${count} errors above.`;
}

function coerceFieldErrorsRecord(
	value: unknown
): Record<string, string> | null {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return null;
	}
	const out: Record<string, string> = {};
	for (const [key, entry] of Object.entries(value)) {
		if (typeof key !== "string" || !key.trim()) {
			continue;
		}
		if (typeof entry === "string" && entry.trim()) {
			out[key] = entry;
		}
	}
	return validationErrorCount(out) > 0 ? out : null;
}

/** Parse field-level errors from a public form submit JSON body. */
export function extractFieldErrorsFromResponse(
	body: unknown
): Record<string, string> | null {
	if (typeof body !== "object" || body === null) {
		return null;
	}
	const record = body as Record<string, unknown>;
	const direct = coerceFieldErrorsRecord(record.errors);
	if (direct) {
		return direct;
	}
	const fieldErrors = coerceFieldErrorsRecord(record.fieldErrors);
	if (fieldErrors) {
		return fieldErrors;
	}
	return null;
}
